import { useState } from "react";
import { ANALYTICS_EVENT, recordEvent } from "../../../hooks/useAnalytics";
import { BOOK_COACHING_URL } from "../../../config/links";
import type { DashboardConcierge } from "../../../pages/dashboard/mock";
import { SupportModal } from "./SupportModal";
import styles from "./ConciergePanel.module.css";

type ConciergePanelProps = {
  readonly concierge: DashboardConcierge;
  readonly tenantId: string | null;
  // Provided only for owners/managers; renders a compact "Restart agent" button in
  // the panel header (opens the confirm modal). Disabled while the instance is still
  // provisioning / locked. The backend re-checks manager access.
  readonly onRestart?: () => void;
  readonly restartDisabled?: boolean;
};

const RefreshIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 2v6h-6" />
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M3 22v-6h6" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
  </svg>
);

const InfoIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const SupportAvatar = (): JSX.Element => (
  <span className={styles.avatar} aria-hidden="true">
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  </span>
);

const CalendarIcon = (): JSX.Element => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

const TOPUP_HOURS_URL = "https://zerotoclaw.ai/hours";

export const ConciergePanel = ({
  concierge,
  tenantId,
  onRestart,
  restartDisabled,
}: ConciergePanelProps): JSX.Element => {
  const [supportOpen, setSupportOpen] = useState<boolean>(false);
  const sessionsLeft = Math.max(0, concierge.sessionsRemaining);
  const hasSessions = sessionsLeft > 0;
  const sessionLabel = sessionsLeft === 1 ? "session" : "sessions";
  // IP-353: an unlimited instance can always book; its credit balance isn't tracked.
  const unlimited = concierge.unlimited;
  const canBook = unlimited || hasSessions;

  const handleBookCoaching = (): void => {
    void recordEvent({
      type: ANALYTICS_EVENT.supportClick,
      subject: tenantId ?? undefined,
      note: "book_coaching_session",
    });
  };
  const handleTopUp = (): void => {
    void recordEvent({
      type: ANALYTICS_EVENT.coachingTopupClick,
      subject: tenantId ?? undefined,
    });
  };
  const handleAskForSupport = (): void => {
    setSupportOpen(true);
    void recordEvent({
      type: ANALYTICS_EVENT.supportClick,
      subject: tenantId ?? undefined,
      note: "ask_for_support",
    });
  };

  return (
    <aside className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.eyebrow}>// SUPPORT</span>
        {onRestart !== undefined ? (
          <button
            type="button"
            className={styles.restartBtn}
            onClick={onRestart}
            disabled={restartDisabled}
            aria-label={
              restartDisabled === true
                ? "Restart agent (available once your instance is ready)"
                : "Restart agent - restarts it if it's stuck or not replying"
            }
            data-testid="instance-restart-button"
          >
            <RefreshIcon />
            <span>Restart agent</span>
            <span className={styles.restartInfo} aria-hidden="true">
              <InfoIcon />
            </span>
            <span className={styles.restartTip} role="tooltip" aria-hidden="true">
              Restarts your agent if it's stuck or not replying. It's back in about a
              minute, with your conversations and settings kept.
            </span>
          </button>
        ) : null}
      </div>

      <div className={styles.top}>
        <SupportAvatar />
        <div className={styles.identity}>
          <p className={styles.heading}>Need a hand?</p>
          <p className={styles.role}>{concierge.teamLabel} — here when you need us.</p>
        </div>
      </div>

      {unlimited ? (
        <div className={styles.includedTag} aria-label="Unlimited coaching sessions">
          <CalendarIcon />
          <span>
            <strong>Unlimited</strong> coaching sessions
          </span>
        </div>
      ) : hasSessions ? (
        <div className={styles.includedTag} aria-label="Coaching sessions remaining">
          <CalendarIcon />
          <span>
            <strong>{sessionsLeft}</strong> coaching {sessionLabel} left
          </span>
        </div>
      ) : (
        <div className={styles.usedUpTag} aria-label="No coaching sessions left">
          <CalendarIcon />
          <span>
            <strong>No coaching sessions left</strong>
          </span>
        </div>
      )}

      <div className={styles.actions}>
        {canBook ? (
          <a
            className={styles.book}
            href={BOOK_COACHING_URL}
            target="_blank"
            rel="noreferrer"
            onClick={handleBookCoaching}
          >
            <span>Book coaching session</span>
            <span aria-hidden="true">→</span>
          </a>
        ) : !hasSessions ? (
          <a
            className={styles.bookMuted}
            href={TOPUP_HOURS_URL}
            target="_blank"
            rel="noreferrer"
            onClick={handleTopUp}
          >
            <span>Top up coaching credits</span>
            <span aria-hidden="true">→</span>
          </a>
        ) : null}
        <button
          type="button"
          className={styles.support}
          onClick={handleAskForSupport}
          aria-haspopup="dialog"
          data-testid="ask-for-support-button"
        >
          <span>Ask for support</span>
          <span aria-hidden="true">→</span>
        </button>
      </div>

      <div className={styles.security} role="status" aria-label="Security overview">
        <svg
          className={styles.shield}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 3l8 3v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-3z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
        <span>
          <strong>Locked down.</strong> Encrypted, isolated, monitored.
        </span>
      </div>

      {supportOpen ? (
        <SupportModal tenantId={tenantId} onClose={() => setSupportOpen(false)} />
      ) : null}
    </aside>
  );
};
